import { useEffect, useRef } from 'react';

// A new item is always appended at the end — scroll it into view so adding one has a visible
// effect, rather than silently landing below the fold. The MutationObserver + settle-timer waits
// out whatever layout shifting happens right after the item mounts (e.g. its own child components
// finishing their first render) before scrolling, instead of scrolling to a position that then
// moves.
export function useScrollNewItemIntoView<T extends HTMLElement>(itemCount: number) {
  const containerRef = useRef<T>(null);
  const previousCountRef = useRef(itemCount);

  useEffect(() => {
    const shouldScroll = itemCount > previousCountRef.current;
    previousCountRef.current = itemCount;
    if (!shouldScroll) return;

    const target = containerRef.current?.lastElementChild;
    if (!target) return;

    let quietTimer: ReturnType<typeof setTimeout>;
    const scrollOnceSettled = () => {
      clearTimeout(quietTimer);
      quietTimer = setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        observer.disconnect();
      }, 100);
    };

    const observer = new MutationObserver(scrollOnceSettled);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    scrollOnceSettled();

    return () => {
      observer.disconnect();
      clearTimeout(quietTimer);
    };
  }, [itemCount]);

  return containerRef;
}
